import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

type Notif = {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  read_at: string | null;
  created_at: string;
  metadata: any;
};

const linkFor = (n: Notif) => {
  if (n.entity_type === "booking" && n.entity_id) return `/admin/bookings/${n.entity_id}`;
  if (n.entity_type === "order" && n.entity_id) return `/admin/orders/${n.entity_id}`;
  return "/admin/notifications";
};

export const AdminNotificationsBell = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<Notif[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const unread = items.filter((i) => !i.read_at).length;

  const load = async () => {
    const { data } = await supabase
      .from("admin_notifications" as any)
      .select("*").order("created_at", { ascending: false }).limit(20);
    setItems(((data as any[]) ?? []) as Notif[]);
  };

  useEffect(() => {
    if (!user) return;
    load();
    // admin_notifications removed from realtime publication for privacy; poll.
    const id = window.setInterval(() => { load(); }, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { window.clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id: string) => {
    await supabase.from("admin_notifications" as any).update({ read_at: new Date().toISOString() }).eq("id", id);
    setItems((x) => x.map((i) => i.id === id ? { ...i, read_at: new Date().toISOString() } : i));
  };

  const markAll = async () => {
    const ids = items.filter((i) => !i.read_at).map((i) => i.id);
    if (ids.length === 0) return;
    await supabase.from("admin_notifications" as any).update({ read_at: new Date().toISOString() }).in("id", ids);
    load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 hover:bg-panel transition-colors"
        aria-label="Admin notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 bg-primary text-primary-foreground text-[10px] font-mono px-1 min-w-[16px] h-4 flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-surface border border-border shadow-lg z-50 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between p-3 border-b border-border">
            <span className="font-display text-xs tracking-widest">NOTIFICATIONS</span>
            <div className="flex gap-2 text-xs">
              <button onClick={markAll} className="text-muted-foreground hover:text-primary">Mark all read</button>
              <Link to="/admin/notifications" className="text-primary" onClick={() => setOpen(false)}>View all</Link>
            </div>
          </div>
          {items.length === 0 && (
            <div className="p-4 text-xs font-mono text-muted-foreground">No notifications yet.</div>
          )}
          {items.map((n) => (
            <Link
              key={n.id}
              to={linkFor(n)}
              onClick={() => { markRead(n.id); setOpen(false); }}
              className={`block p-3 border-b border-border last:border-0 hover:bg-panel transition-colors ${!n.read_at ? "bg-primary/5" : ""}`}
            >
              <div className="flex justify-between gap-2">
                <span className="text-sm font-body">{n.title}</span>
                {!n.read_at && <span className="w-2 h-2 bg-primary mt-1.5 shrink-0" />}
              </div>
              {n.body && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.body}</p>}
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">
                {n.kind} · {new Date(n.created_at).toLocaleString()}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminNotificationsBell;